// caiet UC91 — the stock visualization timeseries. Keyed on the full filter set
// so ACCS-13 URL state and the cache stay aligned. The bucket is never asked
// for: UC91-07 makes the interval decide it, and the server refuses a forced one.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '../../app/api-client';
import { isValidPeriod } from './stock-filters';

export type Granularity = 'day' | 'month' | 'year';
/**
 * Transport only, never a user choice: `site` reads the base table (where lot,
 * reason and site live), `national` the daily aggregate, and `region` and
 * `locality` group the series by place (UC91-10). The server refuses any other.
 */
export type Dimension = 'national' | 'registrar' | 'site' | 'region' | 'locality';

export interface TimeseriesPoint {
  bucket: string;
  quantity: number;
  isProjected: boolean;
  isCarryForward: boolean;
}

/**
 * One unique medicine (CF-34), or one unique medicine in one place under region and
 * locality. `medicineCode` names it: the first selected code of that medicine;
 * `medicineCodes` are the selected codes it stands for
 * (docs/shape-uc-91-series-and-movements.md §2 D1, D2).
 */
export interface TimeseriesSeries {
  medicineCode: string;
  medicineCodes: string[];
  label: string;
  color: string;
  /**
   * The region or locality of a series under those dimensions, '' elsewhere and,
   * under them, for stock whose site has no such place on record.
   */
  groupId: string;
  groupLabel: string;
  points: TimeseriesPoint[];
}

/**
 * caiet UC91-04: one stock row of the unaggregated list, as the server read it.
 * `medicineCode` is the code the row was reported under and `medicineLabel` its
 * label (CF-34); `seriesCode` the series it belongs to.
 */
export interface StockMovement {
  id: string;
  stockDate: string;
  medicineCode: string;
  medicineLabel: string;
  seriesCode: string;
  registrarIdno: string;
  registrarName: string;
  siteId: string;
  siteName: string;
  lot: string;
  lotExpiry: string;
  // 'DAY' | 'MONTH': how the lot's expiry was reported; it decides how the date is shown.
  lotExpiryPrecision?: string;
  quantity: number;
  reason: string;
  isCorrection: boolean;
  // UC91-16: what a correction from the list shows and checks, the report's channel,
  // acceptance time and correction window. Absent from an Observer's rows, which
  // keep the UC91-04 columns only (UC91-17).
  source?: string;
  confirmedAt?: string;
  correctionWindowDays?: number;
}

/** The request parameter a dropped criterion came in, as the server names it. */
export type StockCriterion =
  | 'dimension'
  | 'registrarIds'
  | 'siteIds'
  | 'regionIds'
  | 'localityIds'
  | 'lots';

export interface DroppedCriterion {
  criterion: StockCriterion;
  values: string[];
}

export interface TimeseriesResponse {
  buckets: string[];
  series: TimeseriesSeries[];
  granularity: Granularity;
  granularityIsAuto: boolean;
  dimension: Dimension;
  /**
   * Under the `site` dimension only: the newest rows of the window (the server caps
   * them) and how many matched, so the screen can say when it shows fewer.
   */
  movements: StockMovement[];
  movementCount: number;
  /**
   * caiet ACCS-13 / CF-52: the criteria the server dropped because the active role
   * or Registrar may not use them; empty when it applied them all.
   */
  droppedCriteria?: DroppedCriterion[];
}

export interface TimeseriesFilters {
  medicineCodes: string[];
  from: string;
  to: string;
  dimension: Dimension;
  registrarIds: string[];
  siteIds: string[];
  /** UC91-11: published region and locality ids; the server drops the ones the role may not use. */
  regionIds: string[];
  localityIds: string[];
  lots: string[];
}

function toParams(filters: TimeseriesFilters): Record<string, string> {
  const params: Record<string, string> = {
    medicineCodes: filters.medicineCodes.join(','),
    from: filters.from,
    to: filters.to,
    dimension: filters.dimension,
  };
  if (filters.registrarIds.length > 0) params['registrarIds'] = filters.registrarIds.join(',');
  if (filters.siteIds.length > 0) params['siteIds'] = filters.siteIds.join(',');
  if (filters.regionIds.length > 0) params['regionIds'] = filters.regionIds.join(',');
  if (filters.localityIds.length > 0) params['localityIds'] = filters.localityIds.join(',');
  if (filters.lots.length > 0) params['lots'] = filters.lots.join(',');
  return params;
}

/** A refusal the screen has something specific to say about, e.g. the ceiling. */
export interface TimeseriesRefusal {
  code: string;
  limit?: number;
}

export function isTimeseriesRefusal(value: unknown): value is TimeseriesRefusal {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as TimeseriesRefusal).code === 'string'
  );
}

async function fetchTimeseries(filters: TimeseriesFilters): Promise<TimeseriesResponse> {
  const response = await apiClient.get<TimeseriesResponse | TimeseriesRefusal>(
    '/stock/timeseries',
    {
      params: toParams(filters),
      // A 400 carries the whole domain refusal, and the shared normaliser keeps
      // only its `code` and `message`. Reading the body here is what lets the
      // screen name the ceiling the server actually applied rather than restate a
      // number of its own, which would drift the day the server moves it. 401 is
      // left to the normaliser, which owns the redirect.
      validateStatus: (status) => status < 300 || status === 400,
    },
  );
  if (response.status === 400) {
    throw response.data as TimeseriesRefusal;
  }
  return response.data as TimeseriesResponse;
}

export function useStockTimeseries(filters: TimeseriesFilters): UseQueryResult<TimeseriesResponse> {
  return useQuery({
    queryKey: ['stock-timeseries', filters],
    queryFn: () => fetchTimeseries(filters),
    enabled: filters.medicineCodes.length > 0 && isValidPeriod(filters.from, filters.to),
    staleTime: 30_000,
  });
}
